import { createContext, useEffect, useState } from "react"

const THEMES = ["dark", "light", "system"] as const

type Theme = (typeof THEMES)[number]

const isTheme = (value: unknown): value is Theme =>
  THEMES.includes(value as Theme)

interface ThemeProviderProps {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

interface ThemeProviderState {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const initialState: ThemeProviderState = {
  theme: "system",
  setTheme: () => null,
}

export const ThemeProviderContext =
  createContext<ThemeProviderState>(initialState)

// Browsers with site data blocked (Android in-app WebViews, Chrome with
// "block all cookies", sandboxed documents) throw SecurityError on
// `localStorage` access rather than returning null. Reading it unguarded here
// crashes the initial render and takes the whole app down, so the theme
// preference degrades to the default when storage is unavailable.
const readStoredTheme = (storageKey: string, defaultTheme: Theme): Theme => {
  try {
    const stored = localStorage.getItem(storageKey)
    return isTheme(stored) ? stored : defaultTheme
  } catch {
    return defaultTheme
  }
}

const writeStoredTheme = (storageKey: string, theme: Theme) => {
  try {
    localStorage.setItem(storageKey, theme)
  } catch {
    // Best effort — the theme still applies for the rest of the session.
  }
}

function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "vite-ui-theme",
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(() =>
    readStoredTheme(storageKey, defaultTheme),
  )

  useEffect(() => {
    const root = window.document.documentElement

    root.classList.remove("light", "dark")

    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light"

      root.classList.add(systemTheme)
      return
    }

    root.classList.add(theme)
  }, [theme])

  const value = {
    theme,
    setTheme: (theme: Theme) => {
      writeStoredTheme(storageKey, theme)
      setTheme(theme)
    },
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export { ThemeProvider }
